import React from 'react'
import App, { AppContext, AppInitialProps } from 'next/app'
import Cookies from 'cookie-universal'
import { NextRouter } from 'next/router'
import { env } from '@mc/runtime-env'
import middleware from 'middleware'
import { ParsedUrlQuery } from 'querystring'
import ErrorBoundary from '@mc/error-boundary'
import { ErrorPage } from '@mc/error-page'
import { setExperiments, setExperimentsVariables } from '@mc/experiments'
import { ExperimentsProvider } from '@mc/experiments-react'
import { setFeatureFlips } from '@mc/feature-flip'
import { FeatureFlipsProvider } from '@mc/feature-flip-react'
import { setGeo, setRegion } from '@mc/geo'
import { GeoProvider } from '@mc/geo-react'
import { I18nextProvider } from '@mc/i18n'
import {
  reportError as reportBrowserError,
  initializeReporting,
} from '@mc/monitoring'
import { THEME_COLORS } from '@educator/components/ThemeColor'
import { Head, NewRelic, Segment, Consent } from '@mc/next-common'
import {
  FORCE_BUCKETING_COOKIE,
  getGrowthBookFlags,
} from '@mc/next-growthbook'
import { reportError } from '@mc/next-newrelic'
import { StaticDataProvider } from '@mc/static-data-react'
import {
  CLIENT_APPLICATION_NAMES,
  TRACK_PROPS,
  setServer,
} from '@mc/track-event'
import { SegmentTrackingContext, TrackingParamsProvider } from '@mc/tracking-context'
import { OpenAPI as EducatorOpenAPI } from '@mc/educator-api'
import { OpenAPI as CoreOpenAPI } from '@mc/api'
import type { OpenAPIConfig as CoreOpenAPIConfig } from '@mc/api'
import {
  GlobalErrorModalProvider,
  ReactQueryProvider
} from '@educator/components'
import { mapCurrentAuthTypeToRegistrationMethod } from '@educator/utils/auth'
import { applyDarkForcedBuckets } from '@educator/utils/darkForcedBuckets'
import { type PurchaseAnalytics } from '@educator/utils/pricing'
import { ChatTransitionOverlay } from '../components/ChatTransitionOverlay'
import { CspViolationReporter } from '../components/CspViolationReporter'
import { PaymentUpdatedToast } from '../components/PaymentUpdatedToast'
import { CatchAllError } from '../features/CatchAllError'
import { AuthProvider } from '../contexts/AuthContext'
import { MarketingConsentProvider } from '../contexts/MarketingConsentProvider'
import { i18nInstance } from '../i18n/i18nInstance'
import { SegmentTrackingProvider } from '../tracking/SegmentTrackingProvider'
import { SprigAppProvider } from '../tracking/SprigAppProvider'
import { SprigUserIdentify } from '../tracking/SprigUserIdentify'
import { TrackingAttributes } from '../tracking/educatorTracking'
import { determineUserType, USER_TYPE, USER_TYPE_LABEL } from '../utils/cookies'
import { getUserAttributes } from '../utils/getUserAttributes'

import 'katex/dist/katex.min.css'
import './tailwind.css'
import './base.scss'

require('libs/client/init-env').init() // eslint-disable-line

setServer('nextjs')

// Initialize browser-only error reporting
if (typeof window !== 'undefined') {
  initializeReporting()
}

interface ExtendedCoreOpenAPIConfig extends CoreOpenAPIConfig {
  EDUCATOR_BASE_PATH?: string
}

if (typeof window !== 'undefined') {
  const basePath = env('NEXT_PUBLIC_EDUCATOR_BASE_PATH')
  const apiBase = basePath ? `${basePath}` : ''
  // Educator API calls route through the proxy with /educator/ prefix
  EducatorOpenAPI.BASE = `${apiBase}/api/v1/educator`
  CoreOpenAPI.BASE = env('NEXT_PUBLIC_EDUCATOR_SERVER_URL') || apiBase
  // Store basePath on OpenAPI config for use in request.ts
  ;(CoreOpenAPI as ExtendedCoreOpenAPIConfig).EDUCATOR_BASE_PATH = basePath
}

type PageProps = {
  user?: {email: string,  currentAuthType: string}
  userAttributes: Awaited<ReturnType<typeof getUserAttributes>>
  title?: string
  userType: 'new' | 'existing'
  experiments: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookExperiments']
  experimentsVariables: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookExperimentsVariables']
  featureFlips: Awaited<
    ReturnType<typeof getGrowthBookFlags>
  >['growthBookFeatureFlips']
  productPriceText?: string
  isProductPriceAvailable?: boolean
  purchaseAnalytics?: PurchaseAnalytics
  error?: globalThis.Error & { statusCode: number }
}

type Props =
  | (AppInitialProps<PageProps> & {
      query?: ParsedUrlQuery
    })
  | {
      serverSideInitialPropsError: true
    }

type ComponentType = React.ComponentType<PageProps> & {
  pageSettings?: {
    title?: string
    pageName?: string
    analyticsLabel?: string
    pageLoadIntegrations?: Record<string, boolean>
  }
}

class Layout extends React.Component<
  Props & { Component: ComponentType; router: NextRouter }
> {
  constructor(props: Props & { Component: ComponentType; router: NextRouter }) {
    super(props)
    try {
      if (!('serverSideInitialPropsError' in props)) {
        const {
          userAttributes,
          experiments,
          experimentsVariables,
          featureFlips,
        } = props.pageProps

        // set lib data for react
        setGeo(userAttributes.country)
        setRegion(userAttributes.region)
        setFeatureFlips(featureFlips)
        setExperiments(experiments)
        setExperimentsVariables(experimentsVariables)
      }
    } catch (e) {
      reportError(e, {
        rootLayout: 'true',
        location: 'Layout constructor',
        message: 'Error occurred in _app.page.js Layout constructor',
      })
    }
  }

  static async getInitialProps(appContext: AppContext): Promise<Props> {
    const appProps: AppInitialProps<PageProps> =
      await App.getInitialProps(appContext)
    const { query, req, res } = appContext.ctx

    // runs on server only
    if (req && res) {
      try {
        // Must run before getUserAttributes: mcUserId is read from the access token.
        // Dynamic import keeps tokenRefresh's Node-only deps out of the client bundle.
        const { getOrRefreshAccessToken } = await import('../utils/tokenRefresh')
        await getOrRefreshAccessToken({ req, res })
        middleware(req, res, appContext.router)

        const userAttributes = {
          ...(await getUserAttributes(appContext.ctx, appContext.router)),
          platform_name: 'educator',
        }
        const {
          growthBookExperiments: evaluatedExperiments,
          growthBookExperimentsVariables,
          growthBookFeatureFlips,
        } = await getGrowthBookFlags(userAttributes, appContext.ctx)
        const growthBookExperiments = applyDarkForcedBuckets(
          evaluatedExperiments,
          Cookies(req, res).get(FORCE_BUCKETING_COOKIE, { parseJSON: false }),
          query,
        )
        // Forward geo headers so SSR API calls to Rails resolve geo-based pricing.
        // SSR requests bypass Cloudflare and go directly to Rails, so these
        // must be explicitly forwarded from the incoming browser request.
        CoreOpenAPI.HEADERS = {
          ...(CoreOpenAPI.HEADERS as Record<string, string>),
          'cf-ipcountry': userAttributes.country,
          'region-isocode': userAttributes.region,
        }

        // set lib data for getServerSideProps
        setGeo(userAttributes.country)
        setRegion(userAttributes.region)
        setFeatureFlips({ ...growthBookFeatureFlips })
        setExperiments({ ...growthBookExperiments })
        setExperimentsVariables({
          ...growthBookExperimentsVariables,
        })

        // Determine user type based on current pathname and cookie state
        const userType = determineUserType(appContext.router.pathname, req, res)

        return {
          ...appProps,
          pageProps: {
            ...appProps.pageProps,
            userType,
            experiments: {
              ...growthBookExperiments,
            },
            experimentsVariables: {
              ...growthBookExperimentsVariables,
            },
            featureFlips: {
              ...growthBookFeatureFlips,
            },
            userAttributes,
          },
          query,
        }
      } catch (e) {
        reportError(e, {
          rootLayout: 'true',
          location: 'getInitialProps',
          message: 'Error occurred in _app.page.js getInitialProps',
        })
        return {
          serverSideInitialPropsError: true,
        }
      }
    }

    return { ...appProps }
  }

  render() {
    if ('serverSideInitialPropsError' in this.props) {
      return <ErrorPage />
    }

    const { Component, pageProps, router } = this.props
    const pageTitle = pageProps?.title || Component.pageSettings?.title || 'MasterClass Executive'
    const { userAttributes, user, userType } = pageProps
    const country = userAttributes?.country
    const region = userAttributes?.region
    const experimentsProviderValues = {
      experiments: pageProps?.experiments,
      experimentsVariables: pageProps?.experimentsVariables,
    }
    const featureFlips = pageProps?.featureFlips
    const userTypeLabel =
      userType === USER_TYPE.NEW ? USER_TYPE_LABEL.NEW : USER_TYPE_LABEL.EXISTING

    const canonicalPath = router.asPath?.replace(/[?#].*$/, '') || '/'
    const canonicalUrl = canonicalPath === '/'
      ? 'https://www.masterclass.com/executive'
      : `https://www.masterclass.com/executive${canonicalPath}`

    const mcPageClassName = 'mc-page mc-theme-light'

    // The ErrorBoundary below wraps the shell's <main>, so when it trips this
    // fallback replaces that landmark rather than rendering inside it. Supply
    // the main here: CatchAllError is also returned directly as a page body
    // (enrollment/checkout), where the shell's <main> already wraps it, so the
    // component itself must not own one.
    const errorBoundaryFallback = () => (
      <main className='mc-page__content' id='mc-main'>
        <CatchAllError />
      </main>
    )

    return (
      <>
        <StaticDataProvider
          value={{
            productPriceText: pageProps.productPriceText,
            isProductPriceAvailable: pageProps.isProductPriceAvailable,
          }}
          providerKey={router.asPath}
        >
          <I18nextProvider i18n={i18nInstance} defaultNS='@mc/educator'>
            <GlobalErrorModalProvider>
              <AuthProvider>
                <ErrorBoundary
                  fallback={errorBoundaryFallback}
                  onError={(e) =>
                    reportBrowserError(
                      new globalThis.Error('Layout ErrorBoundary'),
                      {
                        error: String(e),
                        stack: e?.stack as string,
                      },
                    )
                  }
                >
                  <ReactQueryProvider>
                    <SprigAppProvider anonymousId={userAttributes?.userId}>
                      <SprigUserIdentify />
                      <SegmentTrackingProvider>
                    {/* VIEW_CLIENT_APPLICATION_NAME: without it, Mux QoS
                        beacons fall back to 'Core Web' and Executive views are
                        misattributed in the shared Mux Data environment */}
                    <TrackingParamsProvider value={{ product: 'educator', [TRACK_PROPS.VIEW_CLIENT_APPLICATION_NAME]: CLIENT_APPLICATION_NAMES.EXECUTIVE }}>
                    <TrackingAttributes product_name='educator' product='educator' user_type={userTypeLabel} em={user?.email} email={user?.email} registration_method={mapCurrentAuthTypeToRegistrationMethod(user?.currentAuthType)} {...pageProps.purchaseAnalytics}>
                      <FeatureFlipsProvider value={featureFlips}>
                        <ExperimentsProvider value={experimentsProviderValues}>
                          <GeoProvider value={{ country, region }}>
                            <MarketingConsentProvider>
                            <Head>
                              <title>{pageTitle}</title>
                              <meta property='og:title' content={pageTitle} key='og:title' />
                              <meta name='theme-color' content={THEME_COLORS.dark} key='theme-color' />
                              <link rel='canonical' href={canonicalUrl} />
                            </Head>
                            <NewRelic />
                            <CspViolationReporter />
                            <SegmentTrackingContext.Consumer>
                              {({ trackPage }) => (
                                <Segment
                                  platform='next'
                                  analyticsLabel={Component.pageSettings?.analyticsLabel}
                                  analyticsIntegrations={
                                    Component.pageSettings?.pageLoadIntegrations
                                  }
                                  trackPage={trackPage}
                                />
                              )}
                            </SegmentTrackingContext.Consumer>
                            <Consent />
                            <div className={mcPageClassName}>
                              <main className='mc-page__content' id='mc-main'>
                                <Component {...pageProps} />
                              </main>
                            </div>
                            <ChatTransitionOverlay />
                            {/* Global: confirms the card change on the
                                post-redirect page (the destination bounces
                                homepage→enrollment, with no shared layout). */}
                            <PaymentUpdatedToast />
                            </MarketingConsentProvider>
                          </GeoProvider>
                        </ExperimentsProvider>
                      </FeatureFlipsProvider>
                    </TrackingAttributes>
                    </TrackingParamsProvider>
                      </SegmentTrackingProvider>
                    </SprigAppProvider>
                  </ReactQueryProvider>
                </ErrorBoundary>
              </AuthProvider>
            </GlobalErrorModalProvider>
          </I18nextProvider>
        </StaticDataProvider>
      </>
    )
  }
}

export default Layout
